import type { KnowledgeNoteDocument } from '../types/note';
import {
  getSyncChanges,
  getSyncRecord,
  getSyncSnapshot,
  pushSyncMutations,
} from './api';
import {
  database,
  recordKey,
  userMetaKey,
  type ConflictEntry,
  type LocalRecord,
  type OutboxEntry,
} from './database';
import { createSystemTabs } from './note-factories';
import { uploadPendingAssets } from './asset-sync';
import type {
  ServerSyncRecord,
  SyncEntityType,
  SyncPayload,
} from '../types/sync';
import { SYNC_SCOPE_VERSION } from '../types/sync';

export type SyncStatus = 'idle' | 'syncing' | 'offline' | 'conflict';

export interface SyncEngineSnapshot {
  records: LocalRecord[];
  conflicts: ConflictEntry[];
  status: SyncStatus;
  lastSuccessfulSyncAt?: string;
  lastError?: string;
}

type Listener = (snapshot: SyncEngineSnapshot) => void;

export class SyncEngine {
  private listeners = new Set<Listener>();
  private interval?: number;
  private pushTimer?: number;
  private syncing = false;
  private stopped = false;
  private status: SyncStatus = 'idle';
  private lastSuccessfulSyncAt?: string;
  private lastError?: string;
  private channel?: BroadcastChannel;
  private previewMode = false;

  constructor(readonly userId: string) {}

  async start() {
    this.stopped = false;
    if (import.meta.env.DEV) {
      const preview = await import('../test/ui-preview');
      if (this.userId === preview.UI_PREVIEW_USER_ID) {
        this.previewMode = true;
        await preview.seedUIPreview();
        await this.emit();
        return;
      }
    }
    if (!this.channel && 'BroadcastChannel' in window) {
      this.channel = new BroadcastChannel('classmind-web-sync-v1');
      this.channel.onmessage = event => {
        if (event.data?.userId === this.userId) void this.emit();
      };
    }
    await this.prepareSyncScope();
    // 本地库是工作台的离线事实源。首次联网快照失败时也要先提供完整导航，
    // 这些 version=0 的干净占位会被远端快照覆盖，不会提前进入 Outbox。
    await this.ensureLocalSystemTabs();
    await this.emit();
    await this.syncNow();
    this.interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') void this.syncNow();
    }, 5_000);
    window.addEventListener('online', this.handleOnline);
    document.addEventListener('visibilitychange', this.handleVisibility);
  }

  stop() {
    this.stopped = true;
    if (this.interval) window.clearInterval(this.interval);
    if (this.pushTimer) window.clearTimeout(this.pushTimer);
    window.removeEventListener('online', this.handleOnline);
    document.removeEventListener('visibilitychange', this.handleVisibility);
    this.channel?.close();
    this.channel = undefined;
  }

  subscribe(listener: Listener) {
    this.listeners.add(listener);
    void this.snapshot().then(listener);
    return () => this.listeners.delete(listener);
  }

  async upsert(entityType: SyncEntityType, payload: SyncPayload) {
    return this.persist(entityType, payload.id, () => payload);
  }

  /** 只填充尚无正文的文档，检查和写入共用事务，保护生成期间的用户编辑。 */
  async fillEmptyNote(payload: KnowledgeNoteDocument) {
    return this.persist('knowledge.note', payload.id, existing => {
      if (existing?.deleted) throw new Error('总结笔记已删除');
      if (!existing) return payload;
      const note = existing.payload as KnowledgeNoteDocument;
      if (note.blocks.some(block => block.text.trim())) return undefined;
      return { ...note, blocks: payload.blocks, updatedAt: payload.updatedAt };
    });
  }

  /** 在同一个本地事务内读取最新版并合并，避免转录追加覆盖后台整理结果。 */
  async update(entityType: SyncEntityType, id: string, transform: (payload: SyncPayload) => SyncPayload) {
    return this.persist(entityType, id, existing => {
      if (!existing || existing.deleted) throw new Error('内容已删除或尚未加载');
      return transform(existing.payload);
    });
  }

  private async persist(entityType: SyncEntityType, id: string, resolve: (existing?: LocalRecord) => SyncPayload | undefined) {
    const key = recordKey(this.userId, entityType, id);
    const now = new Date().toISOString();
    await database.transaction('rw', database.records, database.outbox, async () => {
      const existing = await database.records.get(key);
      if (existing?.access?.role === 'viewer') {
        throw new Error('这篇共享笔记只有查看权限');
      }
      const payload = resolve(existing);
      if (!payload) return;
      if (payload.id !== id) throw new Error('不能改变内容身份');
      const queued = await database.outbox.get(key);
      const record: LocalRecord = {
        key,
        userId: this.userId,
        entityType,
        entityId: payload.id,
        schemaVersion: payload.schemaVersion,
        version: existing?.version ?? 0,
        cursor: existing?.cursor ?? '0',
        payload,
        serverPayload: existing?.serverPayload,
        dirty: 1,
        deleted: 0,
        updatedAt: now,
        access: existing?.access,
      };
      const outbox: OutboxEntry = {
        key,
        userId: this.userId,
        mutationId: queued?.mutationId ?? crypto.randomUUID(),
        entityType,
        entityId: payload.id,
        operation: 'upsert',
        baseVersion: queued?.baseVersion ?? existing?.version ?? 0,
        schemaVersion: payload.schemaVersion,
        payload,
        basePayload: queued?.basePayload ?? existing?.serverPayload,
        createdAt: queued?.createdAt ?? now,
      };
      await database.records.put(record);
      await database.outbox.put(outbox);
    });
    this.broadcast();
    await this.emit();
    this.schedulePush();
  }

  async delete(entityType: SyncEntityType, entityId: string) {
    const key = recordKey(this.userId, entityType, entityId);
    const now = new Date().toISOString();
    await database.transaction('rw', database.records, database.outbox, async () => {
      const existing = await database.records.get(key);
      if (!existing) return;
      if (existing.access) {
        throw new Error('共享笔记不能由协作者删除');
      }
      if (existing.version === 0) {
        await database.records.delete(key);
        await database.outbox.delete(key);
        return;
      }
      const queued = await database.outbox.get(key);
      await database.records.put({ ...existing, dirty: 1, deleted: 1, updatedAt: now });
      await database.outbox.put({
        key,
        userId: this.userId,
        mutationId: queued?.mutationId ?? crypto.randomUUID(),
        entityType,
        entityId,
        operation: 'delete',
        baseVersion: queued?.baseVersion ?? existing.version,
        schemaVersion: existing.schemaVersion,
        basePayload: queued?.basePayload ?? existing.serverPayload,
        createdAt: queued?.createdAt ?? now,
      });
    });
    this.broadcast();
    await this.emit();
    this.schedulePush();
  }

  async resolveConflict(key: string, choice: 'server' | 'local') {
    const conflict = await database.conflicts.get(key);
    const local = await database.records.get(key);
    if (!conflict || !local) return;
    const remote = await getSyncRecord(conflict.entityType, conflict.entityId);
    if (choice === 'server') {
      await database.transaction('rw', database.records, database.outbox, database.conflicts, async () => {
        await database.records.put(this.localRecordFromServer(remote));
        await database.outbox.delete(key);
        await database.conflicts.delete(key);
      });
    } else {
      const remotePayload = remote.payload as SyncPayload;
      await database.transaction('rw', database.records, database.outbox, database.conflicts, async () => {
        await database.records.put({
          ...local,
          version: remote.version,
          cursor: remote.cursor,
          serverPayload: remotePayload,
          dirty: 1,
          deleted: 0,
        });
        await database.outbox.put({
          key,
          userId: this.userId,
          mutationId: crypto.randomUUID(),
          entityType: local.entityType,
          entityId: local.entityId,
          operation: 'upsert',
          baseVersion: remote.version,
          schemaVersion: local.schemaVersion,
          payload: local.payload,
          basePayload: remotePayload,
          createdAt: new Date().toISOString(),
        });
        await database.conflicts.delete(key);
      });
      this.schedulePush(0);
    }
    this.broadcast();
    await this.emit();
  }

  /** Agent 读取前等待真实同步完成，不能把离线、冲突或待上传当作空工作区。 */
  async synchronizeForAgent(signal: AbortSignal) {
    if (this.previewMode || this.stopped || !navigator.onLine) throw new Error('当前无法同步 Agent 所需资料');
    const started = Date.now();
    while (Date.now() - started < 30_000) {
      signal.throwIfAborted();
      if (!this.syncing) await this.syncNow();
      signal.throwIfAborted();
      if (this.status === 'offline' || this.status === 'conflict') throw new Error('请先处理同步失败或冲突，再使用 Agent');
      const [pending, conflicts, assets] = await Promise.all([
        database.outbox.where('userId').equals(this.userId).count(),
        database.conflicts.where('userId').equals(this.userId).count(),
        database.assets.where('userId').equals(this.userId).filter(asset => asset.status !== 'ready').count(),
      ]);
      if (!this.syncing && this.lastSuccessfulSyncAt && Date.parse(this.lastSuccessfulSyncAt) >= started && !pending && !conflicts && !assets) return;
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    throw new Error('资料仍在同步，请稍后重试');
  }

  async syncNow() {
    if (this.previewMode) return;
    if (this.syncing || this.stopped || !navigator.onLine) {
      if (!navigator.onLine) {
        this.status = 'offline';
        this.lastError = '网络不可用，修改已安全保存在此设备';
        await this.emit();
      }
      return;
    }
    const run = async () => {
      if (this.syncing || this.stopped) return;
      this.syncing = true;
      this.status = 'syncing';
      await this.emit();
      try {
        const didCompleteSnapshot = await this.meta('did-complete-snapshot') === '1';
        if (didCompleteSnapshot) {
          await this.pullChanges();
        } else {
          await this.pullSnapshot();
          await this.ensureSystemTabs();
        }
        await this.pushOutbox();
        await uploadPendingAssets(this.userId);
        await this.pullChanges();
        const conflictCount = await database.conflicts.where('userId').equals(this.userId).count();
        this.status = conflictCount > 0 ? 'conflict' : 'idle';
        this.lastSuccessfulSyncAt = new Date().toISOString();
        this.lastError = undefined;
      } catch (error) {
        this.status = 'offline';
        this.lastError = error instanceof Error ? error.message : '同步失败';
      } finally {
        this.syncing = false;
        this.broadcast();
        await this.emit();
      }
    };

    if ('locks' in navigator) {
      await navigator.locks.request(`classmind-sync-${this.userId}`, { ifAvailable: true }, async lock => {
        if (lock) await run();
      });
    } else {
      await run();
    }
  }

  private async pullSnapshot() {
    let cursor: string | undefined;
    let pageToken: string | undefined;
    do {
      const page = await getSyncSnapshot(cursor, pageToken);
      cursor = page.snapshotCursor;
      await this.applyRemoteRecords(page.records);
      pageToken = page.nextPageToken ?? undefined;
      if (!page.hasMore) break;
    } while (pageToken);
    await this.setMeta('cursor', cursor ?? '0');
    await this.setMeta('did-complete-snapshot', '1');
  }

  private async pullChanges() {
    let cursor = await this.meta('cursor') ?? '0';
    do {
      const page = await getSyncChanges(cursor);
      await this.applyRemoteRecords(page.records);
      await this.applyAccessChanges(page.changes);
      cursor = page.nextCursor;
      await this.setMeta('cursor', cursor);
      if (!page.hasMore) break;
    } while (true);
  }

  private async applyRemoteRecords(records: ServerSyncRecord[]) {
    await database.transaction('rw', database.records, database.outbox, async () => {
      for (const remote of records) {
        const key = recordKey(this.userId, remote.entityType, remote.entityId);
        const local = await database.records.get(key);
        if (local?.dirty) {
          if (JSON.stringify(local.access) !== JSON.stringify(remote.access)) {
            await database.records.put({ ...local, access: remote.access });
          }
          continue;
        }
        if (remote.deletedAt) {
          await database.records.delete(key);
          await database.outbox.delete(key);
          continue;
        }
        await database.records.put(this.localRecordFromServer(remote));
      }
    });
  }

  private localRecordFromServer(remote: ServerSyncRecord): LocalRecord {
    const payload = remote.payload as SyncPayload;
    return {
      key: recordKey(this.userId, remote.entityType, remote.entityId),
      userId: this.userId,
      entityType: remote.entityType,
      entityId: remote.entityId,
      schemaVersion: remote.schemaVersion,
      version: remote.version,
      cursor: remote.cursor,
      payload,
      serverPayload: payload,
      dirty: 0,
      deleted: 0,
      updatedAt: remote.updatedAt,
      access: remote.access,
    };
  }

  private async applyAccessChanges(changes: Array<{
    entityType: SyncEntityType;
    entityId: string;
    operation: 'upsert' | 'delete' | 'grant' | 'revoke';
  }>) {
    const latest = new Map<string, (typeof changes)[number]>();
    for (const change of changes) {
      if (change.operation === 'grant' || change.operation === 'revoke') {
        latest.set(`${change.entityType}:${change.entityId}`, change);
      }
    }
    const revoked = [...latest.values()].filter(change => change.operation === 'revoke');
    if (!revoked.length) return;
    await database.transaction('rw', database.records, database.outbox, database.conflicts, async () => {
      for (const change of revoked) {
        const key = recordKey(this.userId, change.entityType, change.entityId);
        await database.records.delete(key);
        await database.outbox.delete(key);
        await database.conflicts.delete(key);
      }
    });
  }

  private async ensureSystemTabs() {
    const tabs = await database.records
      .where('[userId+entityType]')
      .equals([this.userId, 'knowledge.tab'])
      .toArray();
    const existingByID = new Map(tabs.map(tab => [tab.entityId, tab]));
    for (const tab of createSystemTabs()) {
      const existing = existingByID.get(tab.id);
      if (!existing || (existing.version === 0 && existing.dirty === 0)) {
        await this.upsert('knowledge.tab', tab);
      }
    }
  }

  private async ensureLocalSystemTabs() {
    const now = new Date().toISOString();
    await database.transaction('rw', database.records, async () => {
      for (const tab of createSystemTabs(now)) {
        const key = recordKey(this.userId, 'knowledge.tab', tab.id);
        if (await database.records.get(key)) continue;
        await database.records.put({
          key,
          userId: this.userId,
          entityType: 'knowledge.tab',
          entityId: tab.id,
          schemaVersion: tab.schemaVersion,
          version: 0,
          cursor: '0',
          payload: tab,
          dirty: 0,
          deleted: 0,
          updatedAt: now,
        });
      }
    });
  }

  private async pushOutbox() {
    const entries = (await database.outbox.where('userId').equals(this.userId).sortBy('createdAt')).slice(0, 100);
    if (entries.length === 0) return;
    const response = await pushSyncMutations(entries.map(entry => ({
      mutationId: entry.mutationId,
      entityType: entry.entityType,
      entityId: entry.entityId,
      operation: entry.operation,
      baseVersion: entry.baseVersion,
      schemaVersion: entry.schemaVersion,
      payload: entry.payload,
      basePayload: entry.basePayload,
    })));

    for (const result of response.results) {
      const key = recordKey(this.userId, result.entityType, result.entityId);
      const entry = await database.outbox.get(key);
      const local = await database.records.get(key);
      if (!entry || !local || entry.mutationId !== result.mutationId) continue;
      if (result.status === 'applied') {
        if (entry.operation === 'delete') {
          await database.transaction('rw', database.records, database.outbox, database.conflicts, async () => {
            await database.records.delete(key);
            await database.outbox.delete(key);
            await database.conflicts.delete(key);
          });
          continue;
        }
        if (result.merged) {
          const remote = await getSyncRecord(result.entityType, result.entityId);
          await database.transaction('rw', database.records, database.outbox, database.conflicts, async () => {
            await database.records.put(this.localRecordFromServer(remote));
            await database.outbox.delete(key);
            await database.conflicts.delete(key);
          });
          continue;
        }
        await database.transaction('rw', database.records, database.outbox, database.conflicts, async () => {
          await database.records.put({
            ...local,
            version: result.version,
            cursor: result.cursor ?? local.cursor,
            serverPayload: local.payload,
            dirty: 0,
          });
          await database.outbox.delete(key);
          await database.conflicts.delete(key);
        });
      } else {
        let remotePayload: SyncPayload | undefined;
        let remoteVersion = result.version;
        try {
          const remote = await getSyncRecord(result.entityType, result.entityId);
          if (!remote.deletedAt) remotePayload = remote.payload as SyncPayload;
          remoteVersion = remote.version;
        } catch {
          // 删除/缺失冲突仍保留本地副本，交给用户明确选择。
        }
        await database.transaction('rw', database.outbox, database.conflicts, async () => {
          await database.outbox.delete(key);
          await database.conflicts.put({
            key,
            userId: this.userId,
            entityType: result.entityType,
            entityId: result.entityId,
            reason: result.reason ?? 'version-mismatch',
            conflictingPaths: result.conflictingPaths ?? [],
            basePayload: entry.basePayload,
            localPayload: local.payload,
            remotePayload,
            remoteVersion,
            detectedAt: new Date().toISOString(),
          });
        });
      }
    }
  }

  private async snapshot(): Promise<SyncEngineSnapshot> {
    const [records, conflicts] = await Promise.all([
      database.records.where('userId').equals(this.userId).toArray(),
      database.conflicts.where('userId').equals(this.userId).toArray(),
    ]);
    return {
      records: records.filter(record => !record.deleted),
      conflicts,
      status: conflicts.length > 0 ? 'conflict' : this.status,
      lastSuccessfulSyncAt: this.lastSuccessfulSyncAt,
      lastError: this.lastError,
    };
  }

  private async emit() {
    const snapshot = await this.snapshot();
    for (const listener of this.listeners) listener(snapshot);
  }

  private async prepareSyncScope() {
    if (await this.meta('sync-scope-version') === SYNC_SCOPE_VERSION) return;
    // 保留本地记录和 Outbox，只让下一轮从服务器补拉扩大后的实体范围。
    await database.transaction('rw', database.meta, async () => {
      await database.meta.put({
        key: userMetaKey(this.userId, 'did-complete-snapshot'),
        value: '0',
      });
      await database.meta.put({
        key: userMetaKey(this.userId, 'sync-scope-version'),
        value: SYNC_SCOPE_VERSION,
      });
    });
  }

  private schedulePush(delay = 800) {
    if (this.pushTimer) window.clearTimeout(this.pushTimer);
    this.pushTimer = window.setTimeout(() => void this.syncNow(), delay);
  }

  private broadcast() {
    this.channel?.postMessage({ userId: this.userId, changedAt: Date.now() });
  }

  private async meta(name: string) {
    return (await database.meta.get(userMetaKey(this.userId, name)))?.value;
  }

  private async setMeta(name: string, value: string) {
    await database.meta.put({ key: userMetaKey(this.userId, name), value });
  }

  private readonly handleOnline = () => void this.syncNow();
  private readonly handleVisibility = () => {
    if (document.visibilityState === 'visible') void this.syncNow();
  };
}
